import {Component,OnDestroy,OnInit,ViewChild} from '@angular/core';
import {Balise,TypePorteeBalise} from "@domain/admin/bibliotheque/notification/balise";
import {FloatingButtonAction,TypeAction} from "@share/component/floating-button/floating-button";
import {BehaviorSubject,Subject,takeUntil} from "rxjs";
import {BaliseDetailService} from "@components/admin/bibliotheque/notifications/balises/balise-detail/balise-detail.service";
import {Router} from "@angular/router";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {TranslateService} from "@ngx-translate/core";
import {NgForm} from "@angular/forms";
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";

/**
 * Enum des onglets
 */
export enum Onglets {
    BALISE = "Balise"
}

/**
 * Composant d'affichage de la page détail des balises
 *
 * @author Lionel Gimenez
 * @date 16/03/2026
 */
@Component({
    host: {'data-test-id': 'balise-detail'},
    selector: 'balise-detail',
    templateUrl: './balise-detail.component.html',
    providers: [BaliseDetailService],
})
export class BaliseDetailComponent implements OnInit,OnDestroy {
    /** Balise */
    balise: Balise;

    /** Indicateur de lecture seule (sous-admin) */
    isReadOnly: boolean = false;

    /** Liste statique des onglets */
    public static listeOnglets: Array<PageHeaderItem> = [{
        code: Onglets.BALISE,
        libelle: 'admin.bibliotheque.notifications.balise.detail.onglets.balise',
        url: '/Admin/Bibliotheque/Notification/Balises/'
    }];

    /** Liste des onglets */
    listeTabItems: Array<PageHeaderItem>;

    /** Onglet courant */
    selectedItem: PageHeaderItem = {
        ... BaliseDetailComponent.listeOnglets[0],
        selected: true
    };

    /** Liste des actions possibles pour le floatingButton en bas à droite */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Liste des options de portée */
    listeModuleOption: {code: TypePorteeBalise, libelle: string}[] = [
        {code: TypePorteeBalise.NF, libelle: this.translateService.instant('admin.bibliotheque.notifications.balise.portees.NF')},
        {code: TypePorteeBalise.OD, libelle: this.translateService.instant('admin.bibliotheque.notifications.balise.portees.OD')},
        {code: TypePorteeBalise.FC, libelle: this.translateService.instant('admin.bibliotheque.notifications.balise.portees.FC')},
        {code: TypePorteeBalise.AV, libelle: this.translateService.instant('admin.bibliotheque.notifications.balise.portees.AV')},
        {code: TypePorteeBalise.OT, libelle: this.translateService.instant('admin.bibliotheque.notifications.balise.portees.OT')},
        {code: TypePorteeBalise.DV, libelle: this.translateService.instant('admin.bibliotheque.notifications.balise.portees.DV')},
        {code: TypePorteeBalise.IN, libelle: this.translateService.instant('admin.bibliotheque.notifications.balise.portees.IN')}
    ];

    /** Formulaire de la page */
    @ViewChild('form') form: NgForm;

    /** Subject de destruction */
    private destroy$ = new Subject<void>();

    /**
     * Constructeur
     */
    constructor(
        public baliseDetailService: BaliseDetailService,
        public router: Router,
        public translateService: TranslateService,
        private store: Store<AppState>,
    ) {
    }

    /**
     * Hook initialisation
     */
    ngOnInit(): void {
        //Chargement de la liste des onglets
        this.listeTabItems = [this.selectedItem];

        //Abonnement au chargement de la balise
        this.baliseDetailService.balise$
            .pipe(takeUntil(this.destroy$))
            .subscribe(balise => {
                this.balise = balise;
            });

        //Récupération de la session pour vérifier les droits
        this.store.select(state => state.session).pipe(filterFirstNotNull()).subscribe(session => {
            //Le sous-admin ne peut pas modifier les balises
            this.isReadOnly = !session.isAdmin;

            //Définition de la liste des actions possibles
            this.listeActions.next([{
                type: TypeAction.PRIMARY,
                icone: 'nio icon-sauvegarde',
                libelle: 'global.actions.enregistrer',
                doAction: () => this.baliseDetailService.save(),
                isDisabled: () => !this.form?.valid
            },{
                type: TypeAction.SECONDARY,
                icone: 'nio icon-suppression',
                libelle: 'global.actions.supprimer',
                doAction: () => this.baliseDetailService.deleteBalise(),
                isVisible: () => this.balise != null
                    && !this.baliseDetailService.isCreation
                    && !this.isReadOnly
            }]);
        });

        //On charge la liste des balises
        this.baliseDetailService.loadBaliseByRoute();
    }

    /**
     * Changement d'onglet
     *
     * @param selectedItem Onglet sélectionné
     */
    onSelectedItemChange(selectedItem: PageHeaderItem) {
        //Mise à jour de l'onglet sélectionné
        this.selectedItem = selectedItem;
    }

    /**
     * Handler du retour
     */
    onGoBack() {
        // Retour à la liste des balises
        this.router.navigate(['Admin/Bibliotheque/Notification/Balises']);
    }

    /**
     * Destruction du composant
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}